/**
 * handleSubmit 公共工具函数
 * 统一处理 Chat.tsx 和 ChatPage 的提交逻辑
 */
import { getLocale } from 'umi';
import { find, isEmpty, last, map, some } from 'lodash-es';
import { FILE_DOCUMENT_TYPE, FILE_IMAGE_TYPE, UPLOAD_FILE_IMG_TYPE_LIST } from '../../config/constant';
import type { ISkillData } from '../../SkillsCollection/types';
import { type ISelectedSkillTagData } from '../../components/QAInput/SkillMention/SelectedSkillTag';
import { type ISelectedChartTagData } from '../../components/QAInput/ChartPopover/components/SelectedChartTag';
import { type ISelectedKnowledgeTagData } from '../../components/QAInput/KnowledgeBaseMention/components/SelectedKnowledgeTag';
import { getAgentConfig } from './index';
import { resolveKbExpandToken, buildKnowledgeLogText } from '../../components/QAInput/utils/inlineMentionLabel';
import { Conversation } from '../types/history';
import { getTemplateFlags, type TemplateType } from '@/pages/aigc/utils/utils';

// 重新导出，保持向后兼容
export { getTemplateFlags, type TemplateType };

// ============== 常量 ==============

/** Skill 专用 Agent UUID：当发送消息包含 skill 时，替换 model_id 为此值 */
export const SKILL_AGENT_UUID = 'ec374e72-2cd8-4216-a6ef-fb5087c3a2b4';

/** 特性报表自动绑定的 DataLab skill 默认名 */
const DATALAB_SKILL_DEFAULT_NAME = 'databrain-datalab-analyst';

/** AI Gallery「编辑 HTML」自动绑定的 DIY 报表 skill 默认名（与技能包名 / specified_skills.name 一致） */
const AIGALLERY_DIY_REPORT_SKILL_NAME = 'databrain-diyreport-service';

// ============== 类型定义 ==============

/** 文件项类型 */
export interface IFileItem {
  type?: string;
  originalSrc?: string;
  size?: number;
  name?: string;
}

/** 上传文件 metadata */
export interface IUploadFileMetadata {
  detail: 'auto';
  image_type: string;
  size: number;
  name: string;
}

export interface IChartFileMetadata {
  chart_ids: string[];
  /** 与 chart_ids 同序，打点 chart_name 用（报表名@图表名） */
  chart_names?: string[];
}

/** 处理后的文件数据项 */
export interface IFileListDataItem {
  type: 'file';
  file: {
    mimeType: string;
    uri: string;
  };
  metadata?: IUploadFileMetadata | IChartFileMetadata;
  /** chart entry 的 UI 适配字段；让 frontFileList 复用同一份 chart entry 时 QAInput 的 successList 可直接接住 */
  status?: 'success';
}


/** 服务器参数基础类型 */
export interface IBaseServerParams {
  system_language: string;
  model_id?: string;
  mode?: string;
  chat_engine?: string;
  use_lke_kb?: boolean;
  skill_config?: {
    /** 用户安装的 skill 列表 */
    allowed_skills: Array<{ name: string; }>;
    /** 用户当前 query 使用的 skill 列表 */
    specified_skills: Array<{ name: string; }>;
  };
  frontend_metadata?: {
    field_name?: string;
    type?: string;
    form?: string;
    prefixImage?: string;
    queryTabs?: ISelectedSkillTagData[];
    queryChartsByGame?: Array<{
      gameCode: string;
      gameName?: string;
      gameIcon?: string;
      charts: Array<{
        chartId: string;
        type: string;
        nameCN: string;
        nameEN: string;
      }>;
    }>;
    /** 标记 skill 是否为继承（非用户主动选择） */
    isSkillInherited?: boolean;
    /** 提问区展示用 query（折叠态，与提交前输入一致；模型 query 可能已平铺） */
    display_query?: string;
    /** 外部 SQL/文件 parts，编辑/刷新时回灌 message.parts */
    rawParts?: Array<{ type: 'sql'; text: string; } | IFileListDataItem>;
  };
  /** 知识库开关：有 @ 知识库引用时 enable=true，进入请求 metadata */
  db_args?: {
    enable: boolean;
    /** 知识库引用列表（bk_id=knowledge_base_id，file_ids=knowledge_id） */
    kbs?: IKbArgsItem[];
    /** 外部 SQL/文件 parts，编辑/刷新时回灌 message.parts */
    rawParts?: Array<{ type: 'sql'; text: string; } | IFileListDataItem>;
  };
  function?: Record<string, unknown>;
  /**
   * 请求来源：默认 databrain；经分 bi_dashboard 为 databrain_dashboard；舆情 opinion_pc 为 databrain_summary
   */
  source?: string;
  /**
   * 端标识：Copilot 为 copilot，ChatPage 等页面为 web
   */
  platform?: string;
  [key: string]: unknown;
}

/** 构建 ServerParams 的配置选项 */
export interface IBuildServerParamsOptions {
  /** 用户信息中的 openid */
  userId?: string;
  /** 编辑时传递的模型 ID */
  modelId?: string;
  /** 编辑时传递的模式 */
  mode?: string;
  /** Chat Engine */
  chatEngine?: string;
  /** 外部查询配置 */
  externalQueryConfig?: {
    prefixTag?: string;
    form?: string;
    prefixImage?: string;
    files?: IExternalFileItem[];
    quoted_content?: {
      type: string;
      value: string;
    };
  } | null;
  /** DS 报告模式配置 */
  dsReportConfig?: {
    isDsMode: boolean;
    agentModel?: string;
    systemLanguage?: string;
    initialModelId?: string;
    locale?: string;
  };
  /** AI Excel 模式配置 */
  aiExcelConfig?: {
    isExcel: boolean;
    metadata?: Record<string, unknown>;
  };
  /** 魔法海报模式配置 */
  magicPosterConfig?: {
    isMagicPoster: boolean;
    metadata?: Record<string, unknown>;
  };
  /** Skill 模式配置 */
  skillConfig?: {
    /** 是否为 Skill 模式（由外部 getTemplateFlags 计算后传入） */
    isSkill: boolean;
    /** 选中的 Skills */
    selectedSkills?: ISelectedSkillTagData[];
    /** 用户安装的 Skills（用于 allowed_skills） */
    installedSkills?: Array<{ name: string; }>;
    /** 全量 Skills（特性报表时自动匹配 datalab skill） */
    allSkills?: ISkillData[];
    /** Skill 是否为继承（非用户主动选择） */
    isInherited?: boolean;
  };
  /** 文件列表（用于判断是否需要 use_lke_kb） */
  fileList?: IFileItem[];
  /** 原始图表标签列表（用于构建 frontend_metadata.queryChartsByGame） */
  selectedCharts?: ISelectedChartTagData[];
  /** 知识库引用标签列表（仅用于判断 db_args.enable / display_query） */
  selectedKnowledge?: ISelectedKnowledgeTagData[];
  /** 提交前输入框文案（折叠态），写入 frontend_metadata.display_query */
  displayQuery?: string;
  /** 模型侧平铺 query，与 display_query 不同时写入 kb_expand_token */
  modelQuery?: string;
  /** 额外的服务器属性 */
  serverProps?: Record<string, unknown>;
  /**
   * 端标识，不传默认为 web；Copilot 传入 copilot
   */
  platform?: 'copilot' | 'web';
}

/** 构建日志参数的配置选项 */
export interface IBuildLogParamsOptions {
  /** 基础数据来源 */
  dataSource: string;
  /** 初始日志参数 */
  initialLogParams?: Record<string, unknown>;
  /** 外部查询配置 */
  externalQueryConfig?: {
    form?: string;
  } | null;
  /** DS 报告模式配置 */
  dsReportConfig?: {
    isDsMode: boolean;
    serverParams?: Record<string, unknown>;
    agentName?: string;
  };
  /** AI Excel 模式配置 */
  aiExcelConfig?: {
    isExcel: boolean;
  };
  /** 选中的技能列表 */
  selectedSkills?: ISelectedSkillTagData[];
  /** 文件列表（与 selectedCharts 一并传入，内部解析 chart_name 打点） */
  fileList?: IFileItem[];
  /** 选中的图表标签（与 fileList 一并传入，内部解析 chart_name 打点） */
  selectedCharts?: ISelectedChartTagData[];
  /** 选中的知识库引用（用于 kb_name 打点） */
  selectedKnowledge?: ISelectedKnowledgeTagData[];
  /** 知识库折叠单位（篇 / docs），用于 kb_name 平铺展开 */
  kbDocUnit?: string;
  /** 打点扩展参数（来自 QAInput onSubmit 的 logParams，仅用于埋点上报，不会发送到后端） */
  submitLogParams?: Record<string, unknown>;
}

/** 过滤对话列表的配置 */
export interface IFilterConversationsOptions {
  /** 操作来源 */
  from?: string;
  /** 需要删除的消息 ID 列表 */
  deleteMessageIds?: string[];
  /** 当前对话列表 */
  conversations: Conversation[];
}

// ============== 工具函数 ==============

/** handleSubmit 来源类型 */
export type TSubmitFrom = 'edit' | 'refresh' | 'send' | 'recommend' | 'homeQuestion' | 'copilotRecommend';

/** getEffectiveSkills 配置选项 */
export interface IGetEffectiveSkillsOptions {
  /** 当前选中的技能 */
  selectedSkills?: ISelectedSkillTagData[];
  /** 是否为 Skill 会话 */
  isSkillSession?: boolean;
  /** 对话历史列表 */
  conversations?: Conversation[];
  /** 提交来源（edit/refresh/send/...），不同来源对继承策略有不同处理 */
  from?: TSubmitFrom;
  /** target 消息的 frontend_metadata（refresh 场景用于读取 isSkillInherited，镜像原展示状态） */
  frontendMetadata?: { isSkillInherited?: boolean;[key: string]: unknown; };
}

/** getEffectiveSkills 返回结果 */
export interface IEffectiveSkillsResult {
  /** 最终使用的技能列表 */
  skills: ISelectedSkillTagData[] | undefined;
  /** 是否为继承（非用户主动选择） */
  isInherited: boolean;
}

/**
 * 获取有效的技能配置
 * - edit：用用户编辑后的选择，不继承（输入框按用户当前选择展示）
 * - refresh：按当轮 target 消息的 skills 发给服务端；
 *   isInherited 直接取 target 原本的 frontend_metadata.isSkillInherited，
 *   镜像原消息的展示状态（原本不展示就继续不展示，原本展示就继续展示）
 * - 其他 skill 会话：若当前未选择 skill，则继承上一轮对话的 skill_config
 * @param options - 配置选项
 * @returns 有效的技能列表和是否为继承标记
 */
export const getEffectiveSkills = ({
  selectedSkills,
  isSkillSession,
  conversations,
  from,
  frontendMetadata,
}: IGetEffectiveSkillsOptions): IEffectiveSkillsResult => {
  const hasNoSelectedSkills = isEmpty(selectedSkills);
  const hasConversations = !isEmpty(conversations);

  // 编辑模式下，直接使用用户编辑后的选择，不继承
  if (from === 'edit') {
    return { skills: selectedSkills, isInherited: false };
  }

  // 重新生成：按当轮 skills 发送给服务端，isInherited 镜像 target 原状态
  // target 的 frontend_metadata 由 useRefreshHooks 透传至 serverProps.frontend_metadata
  if (from === 'refresh') {
    return { skills: selectedSkills, isInherited: Boolean(frontendMetadata?.isSkillInherited) };
  }

  // 非 skill 会话或已有选中技能，直接返回
  if (!isSkillSession || !hasNoSelectedSkills || !hasConversations) {
    return { skills: selectedSkills, isInherited: false };
  }

  // 从最后一轮对话中获取 skill_config
  const lastConversation = last(conversations);
  const inheritedSkills = lastConversation?.frontend_metadata?.queryTabs as ISelectedSkillTagData[] | undefined;
  if (!isEmpty(inheritedSkills)) {
    return { skills: inheritedSkills, isInherited: true };
  }

  return { skills: selectedSkills, isInherited: false };
};

/**
 * 获取丰富后的 externalQueryConfig（注入 quoted_content），参照 getEffectiveSkills 继承模式
 * - 普通发送：仅首轮（conversations.length < 1）携带外部参数
 * - edit/refresh：返回 null，由 serverProps（target 消息 metadata）与 buildFileListData editContext 回灌
 */
export const getQuotedContent = (
  conversations: Conversation[],
  externalQueryConfig?: {
    prefixTag?: string;
    form?: string;
    quoted_content?: { type: string; value: string; };
    files?: IExternalFileItem[];
    prefixImage?: string;
  } | null,
  from?: TSubmitFrom,
): {
  prefixTag?: string;
  form?: string;
  quoted_content?: { type: string; value: string; };
  files?: IExternalFileItem[];
  prefixImage?: string;
} | null | undefined => {
  if (from === 'edit' || from === 'refresh') {
    return null;
  }
  if ((conversations?.length ?? 0) >= 1) {
    return null;
  }
  if (externalQueryConfig?.prefixTag) {
    return {
      ...externalQueryConfig,
      quoted_content: {
        type: 'game',
        value: externalQueryConfig.prefixTag,
        ...externalQueryConfig.quoted_content,
      },
    };
  }
  const convWithFieldName = conversations?.find(
    (conv) => conv?.frontend_metadata?.field_name,
  );
  if (convWithFieldName?.frontend_metadata?.field_name) {
    const rawParts: any[] = (convWithFieldName.frontend_metadata as any)?.rawParts || [];
    const filesFromParts: IExternalFileItem[] = rawParts.map((p: any) =>
      p.type === 'sql'
        ? { type: 'sql' as const, text: p.text }
        : { type: 'file' as const, file: { mimeType: p.file?.mimeType, uri: p.file?.uri } },
    );
    return {
      prefixTag: convWithFieldName.frontend_metadata.field_name,
      form: convWithFieldName.frontend_metadata.type,
      quoted_content: {
        type: 'game',
        value: convWithFieldName.frontend_metadata.field_name,
        ...externalQueryConfig?.quoted_content,
      },
      prefixImage: (convWithFieldName.frontend_metadata as any)?.prefixImage,
      files: filesFromParts.length ? filesFromParts : undefined,
    };
  }
  return externalQueryConfig;
};

/**
 * 判断文件是否为图片类型
 * @param fileType - 文件类型
 * @returns 是否为图片
 */
export const isImageFile = (fileType?: string): boolean => {
  return UPLOAD_FILE_IMG_TYPE_LIST.includes(fileType || '');
};

/**
 * 判断文件列表中是否包含非图片文件（需要启用 LKE 知识库）
 * @param fileList - 文件列表
 * @returns 是否需要启用 LKE 知识库
 */
export const shouldUseLkeKb = (fileList?: IFileItem[]): boolean => {
  if (!fileList || fileList.length === 0) return false;
  return fileList.some((item) => !isImageFile(item?.type));
};

// chart_id / chart_name 共用拼接：`${报表}@${图表}`；缺少报表段时退化为图表段
const joinChartPair = (dashboardPart?: string, chartPart?: string) =>
  (dashboardPart ? `${dashboardPart}@${chartPart}` : chartPart || '');

// 派生后端 chart_ids（dashboard 类按 dashboardChartIds 展开）
const flattenSelectedChartIds = (selectedCharts?: ISelectedChartTagData[]): string[] =>
  (selectedCharts || []).flatMap((sc) => {
    const dashboardId = sc.realDashboardId || sc.dashboardValue;
    if (sc.type === 'dashboard') {
      return (sc.dashboardChartIds || []).map((cid) => joinChartPair(dashboardId, cid));
    }
    return sc.chartId ? [joinChartPair(dashboardId, sc.chartId)] : [];
  });

// 打点 chart_name：结构与 flattenSelectedChartIds 完全镜像，仅 id → 中文名
const flattenSelectedChartNames = (selectedCharts?: ISelectedChartTagData[]): string[] =>
  (selectedCharts || []).flatMap((sc) => {
    const dashboardName = (sc.dashboardNameCN || sc.nameCN || '').trim();
    if (sc.type === 'dashboard') {
      const chartIds = sc.dashboardChartIds || [];
      const chartNames = sc.dashboardChartNames || [];
      return chartIds.map((_, idx) =>
        joinChartPair(dashboardName, (chartNames[idx] || dashboardName).trim()),
      );
    }
    const chartName = (sc.nameCN || sc.nameEN || '').trim();
    return sc.chartId ? [joinChartPair(dashboardName, chartName)] : [];
  });

const EMPTY_CHART_REFS: { chartIds: string[]; chartNames: string[]; } = {
  chartIds: [],
  chartNames: [],
};

/** 合并历史 chart part 与当前选中图表，按 chart_id 去重并保持 name 对齐 */
const mergeChartRefs = (
  historyIds: string[],
  historyNames: string[],
  nextIds: string[],
  nextNames: string[],
): { chartIds: string[]; chartNames: string[]; } => {
  if (!historyIds.length && !nextIds.length) {
    return EMPTY_CHART_REFS;
  }
  if (!historyIds.length) {
    return { chartIds: nextIds, chartNames: nextNames };
  }
  if (!nextIds.length) {
    const nameById = new Map<string, string>();
    historyIds.forEach((id, index) => {
      if (!id) return;
      nameById.set(id, historyNames[index] || nameById.get(id) || '');
    });
    const chartIds = Array.from(nameById.keys());
    if (!chartIds.length) {
      return EMPTY_CHART_REFS;
    }
    return {
      chartIds,
      chartNames: chartIds.map((id) => nameById.get(id) || ''),
    };
  }

  const nameById = new Map<string, string>();
  historyIds.forEach((id, index) => {
    if (!id) return;
    nameById.set(id, historyNames[index] || nameById.get(id) || '');
  });
  nextIds.forEach((id, index) => {
    if (!id) return;
    nameById.set(id, nextNames[index] || nameById.get(id) || '');
  });
  const chartIds = Array.from(nameById.keys());
  if (!chartIds.length) {
    return EMPTY_CHART_REFS;
  }
  return {
    chartIds,
    chartNames: chartIds.map((id) => nameById.get(id) || ''),
  };
};

/** fileList + selectedCharts → 合并后的 chart_ids / chart_names（buildFileListData 与打点共用） */
const resolveMergedChartRefs = (
  fileList?: IFileItem[],
  selectedCharts?: ISelectedChartTagData[],
): { chartIds: string[]; chartNames: string[]; } => {
  const list = fileList || [];
  const hasSelectedCharts = !isEmpty(selectedCharts);

  if (!list.length && !hasSelectedCharts) {
    return EMPTY_CHART_REFS;
  }

  let chartIdsFromHistory: string[] = [];
  let chartNamesFromHistory: string[] = [];

  if (list.length) {
    const historyChartParts = list.filter((it: any) => it?.file?.mimeType === 'chart');
    if (!historyChartParts.length && !hasSelectedCharts) {
      return EMPTY_CHART_REFS;
    }
    if (historyChartParts.length) {
      chartIdsFromHistory = historyChartParts.flatMap(
        (it: any) => (it?.metadata?.chart_ids || []) as string[],
      );
      chartNamesFromHistory = historyChartParts.flatMap(
        (it: any) => (it?.metadata?.chart_names || []) as string[],
      );
    }
  }

  if (!chartIdsFromHistory.length && !hasSelectedCharts) {
    return EMPTY_CHART_REFS;
  }

  const nextChartIds = hasSelectedCharts ? flattenSelectedChartIds(selectedCharts) : [];
  if (!chartIdsFromHistory.length && !nextChartIds.length) {
    return EMPTY_CHART_REFS;
  }

  const nextChartNames = hasSelectedCharts ? flattenSelectedChartNames(selectedCharts) : [];

  return mergeChartRefs(
    chartIdsFromHistory,
    chartNamesFromHistory,
    nextChartIds,
    nextChartNames,
  );
};

// 普通文件 UI 项 → 后端 part 结构
const toNormalServerPart = (item: any): IFileListDataItem => ({
  type: 'file',
  file: {
    mimeType: isImageFile(item?.type) ? FILE_IMAGE_TYPE : FILE_DOCUMENT_TYPE,
    uri: item?.originalSrc || '',
  },
  metadata: {
    detail: 'auto',
    image_type: item?.type || '',
    size: item?.size || 0,
    name: item?.name || '',
  },
});

/** 外部文件项类型（直接使用后端协议格式，与 ICopilotExternalQueryConfig.files 对应） */
export interface IExternalFileItem {
  type: 'sql' | 'file';
  text?: string;
  file?: {
    mimeType: string;
    uri: string;
  };
  /** 展示用文件名（如 "report.xlsx"），不传则从 uri 解析 */
  name?: string;
  /** 展示用文件大小（byte），不传则默认 0 */
  size?: number;
}

/**
 * 外部 files → parts 拆分（直接透传协议格式，不做转换）：
 * - type:"sql" → sqlParts
 * - type:"file" → fileParts
 */
export const buildExternalFileParts = (
  files?: IExternalFileItem[],
): { sqlParts: Array<{ type: 'sql'; text: string; }>; fileParts: IFileListDataItem[]; hasParts: boolean; } => {
  const sqlParts: Array<{ type: 'sql'; text: string; }> = [];
  const fileParts: IFileListDataItem[] = [];

  (files || []).forEach((item) => {
    if (item.type === 'sql') {
      sqlParts.push({ type: 'sql', text: item.text || '' });
    } else if (item.type === 'file' && item.file) {
      fileParts.push({
        type: 'file',
        file: { mimeType: item.file.mimeType, uri: item.file.uri },
      });
    }
  });

  const hasParts = sqlParts.length > 0 || fileParts.length > 0;
  return { sqlParts, fileParts, hasParts };
};

/**
 * 一次性产出两份规范化结果：
 * - `serverParts`：发后端 message.parts 用的 part 结构（普通文件被重构，含唯一合并 chart entry + 外部 files）
 * - `frontFileList`：本地 conversation.frontFileList 用的列表（普通文件**保留原 UI 项**
 *   含 `fileSrc/previewName/id` 等渲染字段，chart entry 与 serverParts 共用同一对象）
 *
 * 内置规范化：① 剔除 fileList 中所有旧 chart parts；② 合并旧 chart_ids 与 selectedCharts 派生
 * chart_ids 到唯一 chart entry（带 `status:'success'`，下次 edit 时 QAInput 的 `successList`
 * 按 `status==='success'` 过滤能接住，避免 chart_ids 丢失）。
 * ③ 有 externalFiles 时追加外部 SQL/file parts（在文本 part 之后、普通文件 part 之前）。
 */
export const buildFileListData = (
  fileList?: IFileItem[],
  selectedCharts?: ISelectedChartTagData[],
  externalFiles?: IExternalFileItem[],
  editContext?: { from?: string; deleteMessageIds?: string[]; conversations?: Conversation[]; },
): { serverParts: Array<IFileListDataItem | { type: 'sql'; text: string; }>; frontFileList: any[]; hasExternalParts: boolean; } => {
  let effectiveFiles = externalFiles;
  const effectiveFileList = fileList;

  // 编辑/刷新时从目标消息的 rawParts 提取 SQL + 文件回灌
  if (editContext && (editContext.from === 'edit' || editContext.from === 'refresh') && editContext.deleteMessageIds?.length) {
    const targetConv = editContext.conversations?.find(c => c.messageId === editContext.deleteMessageIds?.[0]);
    const rawParts: any[] = (targetConv as any)?.frontend_metadata?.rawParts || [];
    if (rawParts.length) {
      effectiveFiles = rawParts.map((p: any) =>
        p.type === 'sql'
          ? { type: 'sql' as const, text: p.text }
          : { type: 'file' as const, file: { mimeType: p.file?.mimeType, uri: p.file?.uri } },
      );
    }
  }

  const list = effectiveFileList || [];
  const normalFiles = list.filter((it: any) => it?.file?.mimeType !== 'chart');
  const { chartIds, chartNames } = resolveMergedChartRefs(list, selectedCharts);
  const chartEntry: IFileListDataItem[] = chartIds.length
    ? [{
      type: 'file',
      file: { mimeType: 'chart', uri: '' },
      metadata: { chart_ids: chartIds, chart_names: chartNames },
      status: 'success',
    }]
    : [];

  // 外部 files → sqlParts + fileParts（只送后端，不入 frontFileList）
  const { sqlParts: externalSqlParts, fileParts: externalFileParts, hasParts } = buildExternalFileParts(effectiveFiles);

  return {
    serverParts: [...externalSqlParts, ...externalFileParts, ...normalFiles.map(toNormalServerPart), ...chartEntry],
    frontFileList: [...normalFiles, ...chartEntry],
    hasExternalParts: hasParts,
  };
};

/**
 * 将图表 tag 列表按 gameCode 分组为 frontend_metadata.queryChartsByGame
 * @param selectedCharts - 原始图表标签列表
 * @returns 按游戏分组后的图表数据
 */
export const buildQueryChartsByGame = (selectedCharts?: ISelectedChartTagData[]) => {
  if (!selectedCharts?.length) {
    return [];
  }

  const result: Array<{
    gameCode: string;
    gameName?: string;
    gameIcon?: string;
    charts: Array<{
      chartId: string;
      type: string;
      nameCN: string;
      nameEN: string;
    }>;
  }> = [];

  const findOrCreateGameItem = (gameCode: string, gameName?: string, gameIcon?: string) => {
    let gameItem = result.find((item) => item.gameCode === gameCode);
    if (!gameItem) {
      gameItem = { gameCode, gameName, gameIcon, charts: [] };
      result.push(gameItem);
      return gameItem;
    }

    gameItem.gameName = gameItem.gameName || gameName;
    gameItem.gameIcon = gameItem.gameIcon || gameIcon;
    return gameItem;
  };

  const appendChart = (
    gameItem: {
      charts: Array<{
        chartId: string;
        type: string;
        nameCN: string;
        nameEN: string;
      }>;
    },
    chartInfo: {
      chartId: string;
      type?: string;
      nameCN?: string;
      nameEN?: string;
    },
  ) => {
    const chartId = chartInfo.chartId || '';
    if (!chartId) return;

    const existedChart = gameItem.charts.find((chart) => chart.chartId === chartId);
    if (existedChart) {
      existedChart.type = existedChart.type || chartInfo.type || '';
      existedChart.nameCN = existedChart.nameCN || chartInfo.nameCN || '';
      existedChart.nameEN = existedChart.nameEN || chartInfo.nameEN || '';
      return;
    }

    gameItem.charts.push({
      chartId,
      type: chartInfo.type || '',
      nameCN: chartInfo.nameCN || '',
      nameEN: chartInfo.nameEN || '',
    });
  };

  (selectedCharts || []).forEach((selectedChart) => {
    const gameCode = selectedChart.gameCode || '';
    if (!gameCode) return;

    const gameItem = findOrCreateGameItem(gameCode, selectedChart.gameName, selectedChart.gameIcon);

    // dashboard 类型聚合为 1 条；字段优先级同 flattenSelectedChartIds
    if (selectedChart.type === 'dashboard') {
      appendChart(gameItem, {
        chartId: selectedChart.realDashboardId || selectedChart.dashboardValue || selectedChart.chartId || '',
        type: 'dashboard',
        nameCN: selectedChart.nameCN || '',
        nameEN: selectedChart.nameEN || '',
      });
      return;
    }

    appendChart(gameItem, {
      chartId: selectedChart.chartId || '',
      type: selectedChart.chartRealType || '',
      nameCN: selectedChart.nameCN || '',
      nameEN: selectedChart.nameEN || '',
    });
  });

  return result;
};

/** db_args.kbs.file_list 单个文档项（file_ids=knowledge_id） */
export interface IFileListItem {
  /** 文档 ID（knowledge_id） */
  file_ids: string;
  /** 文件名称 */
  file_name?: string;
  /** 文件类型（md / pdf / docx 等） */
  file_type?: string;
}

/** db_args.kbs 单个知识库项（bk_id=knowledge_base_id，file_ids=knowledge_id） */
export interface IKbArgsItem {
  /** 知识库 ID（knowledge_base_id） */
  bk_id: string;
  /** 知识库名称 */
  name: string;
  /** 空间 ID（tenant_id） */
  tenant_id?: string;
  /** 文档列表；为空数组表示引用整个知识库 */
  file_list: IFileListItem[];
}

/**
 * 按知识库分组构建 db_args.kbs
 * - 同一知识库的多篇文档合并进 file_list
 * - 存在整库引用（docId 为空）时 file_list 置空，表示引用整个知识库
 * @param selectedKnowledge - 已选知识库文档列表
 * @returns kbs 数组
 */
const buildKbArgsList = (
  selectedKnowledge?: ISelectedKnowledgeTagData[],
): IKbArgsItem[] => {
  const list = selectedKnowledge ?? [];
  const kbGroup = new Map<string, ISelectedKnowledgeTagData[]>();
  list.forEach((item) => {
    const group = kbGroup.get(item.kbId) ?? [];
    group.push(item);
    kbGroup.set(item.kbId, group);
  });
  return Array.from(kbGroup.values()).map((items) => {
    const first = items[0];
    // 引用整个知识库时 file_list 为空数组
    const wholeKb = items.some((item) => !item.docId);
    const fileList = wholeKb
      ? []
      : items
        .filter((item) => item.docId)
        .map((item) => ({
          file_ids: item.docId as string,
          file_name: item.fileName,
          file_type: item.fileType,
        }));
    return {
      bk_id: first.kbId,
      name: first.kbName,
      tenant_id: first.tenantId,
      file_list: fileList,
    };
  });
};

/**
 * 构建知识库检索参数：db_args + frontend_metadata.display_query
 * @param selectedKnowledge - 已选知识库文档列表
 * @param displayQuery - 展示用查询文本
 * @returns 知识库参数对象（无引用时返回空对象）
 */
export const buildKnowledgeParams = (
  selectedKnowledge?: ISelectedKnowledgeTagData[],
  displayQuery?: string,
): { db_args?: { enable: boolean; kbs?: IKbArgsItem[]; }; display_query?: string; } => {
  const resolvedQuery = displayQuery ?? '';
  const hasKnowledge = (selectedKnowledge?.length ?? 0) > 0
    || /@[^\s@]+/.test(resolvedQuery);
  if (!hasKnowledge) return {};
  return {
    db_args: { enable: true, kbs: buildKbArgsList(selectedKnowledge) },
    display_query: resolvedQuery,
  };
};

/** skill_config 精简格式（仅 name，用于 serverParams.skill_config） */
export interface ISkillConfigSimple {
  /** 用户安装的 skill 列表 */
  allowed_skills: Array<{ name: string; }>;
  /** 用户当前 query 使用的 skill 列表 */
  specified_skills: Array<{ name: string; }>;
}

/** skill_config 完整格式（含 icon 等，用于 frontend_metadata） */
export interface ISkillConfigFull {
  queryTabs: ISelectedSkillTagData[];
}

/**
 * 构建 skill_config 配置（两种格式）
 * @param selectedSkills - 选中的技能列表
 * @param installedSkills - 用户安装的技能列表（用于 allowed_skills）
 * @param isSkill - 是否为 skill 模式（由 getTemplateFlags 返回）
 * @returns simple: 精简格式（skill_config 使用）；full: 完整格式（frontend_metadata 使用）
 */
export const buildSkillConfig = (
  selectedSkills?: ISelectedSkillTagData[],
  installedSkills?: Array<{ name: string; }>,
  isSkill?: boolean,
): { simple: ISkillConfigSimple; full: Partial<ISkillConfigFull>; } | undefined => {
  // 非 skill 模式，提前返回
  if (!isSkill) return undefined;

  const skills = selectedSkills || [];

  return {
    simple: {
      allowed_skills: installedSkills || [],
      specified_skills: skills.map((s) => ({ name: s?.name })),
    },
    // TODO(后续下线): frontend_metadata.queryTabs 将下线；Skill 已改为 display_query 内联 `/名`，暂保留写入以兼容历史回显与编辑
    // 空数组不写 queryTabs，避免 step 7 用 [] 盖掉 step 10 深合并保留的历史 skill（edit 再次编辑回显）
    full: isEmpty(skills) ? {} : { queryTabs: skills },
  };
};

/**
 * 解析提交 source
 * @param form - 外部引用类型（bi_dashboard / opinion_pc 等）
 * @param hasExternalFiles - 是否带有外部 SQL/文件
 * @returns 提交给后端的 source
 */
const resolveSubmitSource = (form?: string, hasExternalFiles?: boolean): string => {
  if (form === 'bi_dashboard') {
    return 'databrain_dashboard';
  }
  if (form === 'opinion_pc' || hasExternalFiles) {
    return 'databrain_summary';
  }
  return 'databrain';
};

/**
 * 构建服务器参数（核心函数）
 * @param options - 配置选项
 * @returns 构建好的服务器参数
 */
export const buildServerParams = (options: IBuildServerParamsOptions): IBaseServerParams => {
  const {
    userId,
    modelId,
    mode,
    externalQueryConfig,
    dsReportConfig,
    aiExcelConfig,
    magicPosterConfig,
    skillConfig,
    fileList,
    selectedCharts,
    selectedKnowledge,
    displayQuery,
    modelQuery,
    serverProps,
    platform,
  } = options;

  // 解构 skillConfig
  const { isSkill, selectedSkills, installedSkills, isInherited: isSkillInherited = false } = skillConfig || {};

  // 1. 初始化基础参数（含 source / platform）
  let serverParams: IBaseServerParams = {
    system_language: getLocale(),
    source: resolveSubmitSource(
      externalQueryConfig?.form,
      Boolean(externalQueryConfig?.files?.length),
    ),
    platform: platform || 'web',
  };

  // 2. 处理 DS 报告模式（优先级最高，会覆盖其他配置）
  if (dsReportConfig?.isDsMode) {
    const { agentModel, systemLanguage, initialModelId, locale } = dsReportConfig;
    serverParams = {
      ...serverParams,
      model_id: agentModel || initialModelId,
      system_language: systemLanguage || locale || getLocale(),
      mode: 'fastqa',
    };
  } else {
    // 3. 获取 Agent 配置信息
    const { selectedAgentId, currentMode, chatEngine } = getAgentConfig({
      modelId,
      mode,
      userId,
    });

    // 4. 组装通用模式参数
    serverParams = {
      ...serverParams,
      model_id: selectedAgentId,
      mode: currentMode,
      ...(chatEngine && { chat_engine: chatEngine }),
    };
    // 5. 添加外部查询配置（frontend_metadata & quoted_content）
    if (externalQueryConfig) {
      serverParams.frontend_metadata = {
        ...(serverParams.frontend_metadata || {}),
        field_name: externalQueryConfig.prefixTag,
        type: externalQueryConfig.form,
        form: externalQueryConfig.form,
        prefixImage: externalQueryConfig.prefixImage,
      };
      // quoted_content 由外层 getQuotedContent 计算并注入到 externalQueryConfig，此处直接透传
      if (externalQueryConfig.quoted_content) {
        serverParams.quoted_content = externalQueryConfig.quoted_content;
      }
    }

    // 6. AI Excel / 魔法海报 / 图表切换模式追加 function 字段
    const functionName =
      (aiExcelConfig?.isExcel && 'ai_excel') ||
      (magicPosterConfig?.isMagicPoster && 'magic_poster') ||
      ((selectedCharts?.length ?? 0) > 0 && 'chart');
    if (functionName) {
      serverParams.function = {
        name: functionName,
        source: 'main_chat',
        ...serverParams.function || {},
        ...serverProps?.function || {},
      };
    }
  }


  // 7. 添加 skill_config（如果是 skill 模式）
  const builtSkillConfig = buildSkillConfig(selectedSkills, installedSkills, isSkill);
  if (builtSkillConfig) {
    serverParams.skill_config = builtSkillConfig?.simple;
    // 同步追加到 frontend_metadata，保留已有字段（如 field_name、type）
    serverParams.frontend_metadata = {
      ...(serverParams.frontend_metadata || {}),
      ...builtSkillConfig?.full,
      isSkillInherited,
    };
    // 当存在 skill 时，替换 model_id 为指定的 Skill 专用 Agent UUID
    serverParams.model_id = SKILL_AGENT_UUID;
  } else if (isSkill) {
    // 历史 Skill 会话：selectedSkills 为空，但通过 isSkill 识别
    serverParams.model_id = SKILL_AGENT_UUID;
  }

  // 8. 添加图表分组信息到 frontend_metadata
  const queryChartsByGame = buildQueryChartsByGame(selectedCharts);
  if (queryChartsByGame.length > 0) {
    serverParams.frontend_metadata = {
      ...(serverParams.frontend_metadata || {}),
      type: 'custom_dashboard',
      queryChartsByGame,
    };
    // Chart 与 Skill 共用 Skill 专用 Agent UUID，确保历史会话 agent_type=skill_agent，回访时显示按钮
    serverParams.model_id = SKILL_AGENT_UUID;
  }

  // 有 chart_ids 时自动带上 datalab skill（无需用户手动选择）
  if (
    !isEmpty(queryChartsByGame)
    || some(
      fileList,
      (it: any) => it?.file?.mimeType === 'chart' && !isEmpty(it?.metadata?.chart_ids),
    )
  ) {
    const datalabName =
      find(skillConfig?.allSkills, (s: any) => s.skill_key === 'datalab')?.skill_name
      ?? find(skillConfig?.allSkills, { skill_name: DATALAB_SKILL_DEFAULT_NAME })?.skill_name
      ?? DATALAB_SKILL_DEFAULT_NAME;
    const specified =
      serverParams.skill_config?.specified_skills
      ?? map(selectedSkills, (s) => ({ name: s.name }));
    if (!some(specified, { name: datalabName })) {
      serverParams.skill_config = {
        allowed_skills: installedSkills || [],
        specified_skills: [...specified, { name: datalabName }],
      };
    }
  }

  // AI Gallery「编辑 HTML」：按 form 默认带上 DIY 报表 skill（对齐上方 datalab 自动追加，不展示输入框）
  if (externalQueryConfig?.form === 'aigallery_report') {
    const diyName =
      find(skillConfig?.allSkills, { skill_name: AIGALLERY_DIY_REPORT_SKILL_NAME })?.skill_name
      ?? AIGALLERY_DIY_REPORT_SKILL_NAME;
    const specified =
      serverParams.skill_config?.specified_skills
      ?? map(selectedSkills, (s) => ({ name: s.name }));
    if (!some(specified, { name: diyName })) {
      serverParams.skill_config = {
        allowed_skills: installedSkills || [],
        specified_skills: [...specified, { name: diyName }],
      };
    }
    // 与显式选 skill / chart 一致：走 Skill Agent
    serverParams.model_id = SKILL_AGENT_UUID;
  }

  // 9. 知识库 db_args；气泡展示始终保存输入框折叠态（含 / @ # 引用串）
  const knowledgeParams = buildKnowledgeParams(selectedKnowledge, displayQuery);
  if (knowledgeParams.db_args) {
    // 编辑/刷新场景 serverProps 已透传历史 db_args；
    // 当本次 selectedKnowledge 还原失败（kbs 为空）时保留透传的 kbs，避免 file_list 丢失
    const propagatedKbs = (serverProps as
      | { db_args?: { kbs?: IKbArgsItem[]; }; }
      | undefined)?.db_args?.kbs;
    serverParams.db_args = {
      ...((serverProps as { db_args?: Record<string, unknown>; } | undefined)?.db_args || {}),
      ...knowledgeParams.db_args,
      kbs: knowledgeParams.db_args.kbs?.length
        ? knowledgeParams.db_args.kbs
        : propagatedKbs || [],
    };
  }
  if (displayQuery?.trim()) {
    const trimmedDisplay = displayQuery.trim();
    const trimmedModel = modelQuery?.trim() || '';
    const kbExpandToken = trimmedModel
      ? resolveKbExpandToken(trimmedDisplay, trimmedModel)
      : null;
    serverParams.frontend_metadata = {
      ...(serverParams.frontend_metadata || {}),
      display_query: trimmedDisplay,
      ...(kbExpandToken && { kb_expand_token: kbExpandToken }),
    };
  }

  // 10. 判断是否需要启用 LKE 知识库
  if (shouldUseLkeKb(fileList)) {
    serverParams.use_lke_kb = true;
  }

  // 11. 有外部文件/SQL 时缓存 rawParts 供编辑/刷新回灌
  if (externalQueryConfig?.files?.length) {
    const { sqlParts, fileParts } = buildExternalFileParts(externalQueryConfig.files);
    serverParams.frontend_metadata = {
      ...(serverParams.frontend_metadata || {}),
      rawParts: [...sqlParts, ...fileParts],
    };
  }

  // step 7 须配合 buildSkillConfig：editSkills 为空时不写 queryTabs，避免用 [] 盖掉历史 skill。
  // TODO(后续下线): queryTabs 写入将移除，Skill 引用以 display_query 内联 `/名` 为准。
  if (serverProps) {
    const propagatedFrontendMetadata = (serverProps as
      | { frontend_metadata?: IBaseServerParams['frontend_metadata']; }
      | undefined)?.frontend_metadata;

    serverParams = {
      ...(serverProps as Record<string, unknown>),
      ...serverParams,
      frontend_metadata: {
        ...(propagatedFrontendMetadata || {}),
        ...(serverParams.frontend_metadata || {}),
      },
    };
  }

  return serverParams;
};

/**
 * 构建日志参数
 * @param options - 配置选项
 * @returns 构建好的日志参数
 */
export const buildLogParams = (options: IBuildLogParamsOptions): Record<string, unknown> => {
  const {
    dataSource,
    initialLogParams,
    externalQueryConfig,
    dsReportConfig,
    aiExcelConfig,
    selectedSkills,
    fileList,
    selectedCharts,
    selectedKnowledge,
    kbDocUnit,
    submitLogParams,
  } = options;

  // 1. 初始化日志参数
  let logParams: Record<string, unknown> = {
    dataSource,
    ...(initialLogParams || {}),
  };

  // 2. DS 报告模式下的日志参数
  if (dsReportConfig?.isDsMode) {
    logParams = {
      ...dsReportConfig.serverParams,
      dataSource,
      dsTemplateName: dsReportConfig.agentName,
    };
  } else if (externalQueryConfig) {
    // 3. 外部查询配置下更新 dataSource
    logParams.dataSource = externalQueryConfig.form;
  }

  // 4. 选中 Skills 时追加 skill_name
  if (selectedSkills && selectedSkills?.length > 0) {
    logParams.skill_name = selectedSkills.map((s) => s.name).join(',');
  }

  // 4.1 选中知识库时追加 kb_name（平铺库名+文档，与 model query 知识库段一致）
  if (selectedKnowledge && selectedKnowledge.length > 0) {
    const kbLogText = buildKnowledgeLogText(selectedKnowledge, kbDocUnit ?? '篇');
    if (kbLogText) {
      logParams.kb_name = kbLogText;
    }
  }

  // 5. 图表引用打点 chart_name（与 buildFileListData 共用 resolveMergedChartRefs）
  const { chartNames } = resolveMergedChartRefs(fileList, selectedCharts);
  const chartNameLog = chartNames.filter(Boolean).join(',');
  if (chartNameLog) {
    logParams.chart_name = chartNameLog;
  }

  // 6. AI Excel 模式：统一追加打点标识
  if (aiExcelConfig?.isExcel) {
    logParams.name = 'ai_excel';
  }

  // 7. 合并 QAInput 传来的打点扩展参数（覆盖 initialLogParams 的同名字段，优先级最高）
  if (submitLogParams) {
    Object.assign(logParams, submitLogParams);
  }

  return logParams;
};

/**
 * 根据操作类型过滤对话列表
 * @param options - 配置选项
 * @returns 过滤后的对话列表
 */
export const filterConversationsByAction = (
  options: IFilterConversationsOptions
): Conversation[] => {
  const { from, deleteMessageIds, conversations } = options;

  // 只有编辑和刷新操作需要过滤
  if (from === 'edit' || from === 'refresh') {
    return conversations.filter((item) => !deleteMessageIds?.includes(item?.messageId));
  }

  return [];
};

/**
 * 获取埋点来源映射
 * @param from - 操作来源
 * @returns 埋点来源字符串
 */
export const getBuriedLogFrom = (from?: string): string | undefined => {
  const buriedMapKey: Record<string, string> = {
    edit: 'copilotEdit',
    refresh: 'copilotRefresh',
  };

  return (from && buriedMapKey[from]) || from;
};
